// Libraries
import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { createElement, ReactElement } from 'react';
// Types
import { ProductCardProps } from './ProductCard.types';
import { iconsMap } from '@/src/utils/config';

/**
 * ProductCard component displays a product/tool launcher card with an icon,
 * title, description, and a call-to-action label. The whole card is a link,
 * so clicking anywhere on it navigates to `href`.
 *
 * @component
 * @param {NavigationIcons} props.Icon - Icon component for the product.
 * @param {[string, string]} props.titleLines - Two-line title for the card.
 * @param {string} props.description - Short description of the product.
 * @param {string} props.ctaLabel - Label for the call-to-action text.
 * @param {string} props.href - Destination URL the card links to.
 * @returns {ReactElement}
 */
export const ProductCard = ({
  icon,
  titleLines,
  description,
  ctaLabel,
  href,
  badge,
  variant = 'default',
}: ProductCardProps): ReactElement => {
  const isFeatured = variant === 'featured';
  const textColor = isFeatured ? 'white' : 'text.faded';
  const ctaColor = isFeatured ? 'white' : 'link.base';
  return (
    <Link
      href={href}
      display='block'
      position='relative'
      w={'270px'}
      h='179px'
      textDecoration='none'
      _hover={{ textDecoration: 'none' }}
      _focusVisible={{ boxShadow: 'outline', outline: 'none', borderRadius: 'md' }}
      data-testid={`product-card-${icon}`}
      aria-label={`${titleLines.filter(Boolean).join(' ')} - ${ctaLabel}`}
    >
      <Box
        position='absolute'
        inset={0}
        overflow='hidden'
        boxShadow='4px 4px 8px rgba(0, 0, 0, 0.25)'
        borderRadius='md'
        border={isFeatured ? 'none' : 'section'}
        bg={isFeatured ? 'tealishBlue' : 'backgrounds.white'}
        p={5}
        transition='box-shadow 0.15s ease, transform 0.15s ease'
        _hover={{ boxShadow: '6px 6px 12px rgba(0, 0, 0, 0.3)', transform: 'translateY(-2px)' }}
      >
        {/* Card content */}
        <Flex position='relative' direction='column' justify='space-between' h='full'>
          {/* Icon + Title row */}
          <Flex align='flex-start' gap='12px' alignItems='center'>
            <Flex
              w='40px'
              h='40px'
              flexShrink={0}
              alignItems='center'
              justifyContent='center'
              bg='#D8EFF3'
              borderRadius='md'
            >
              {createElement(iconsMap[icon], { w: 8, h: 8, color: 'base' })}
            </Flex>
            <Flex direction='column'>
              <Text fontSize='hs' fontWeight='bold' color={textColor} lineHeight='1.2'>
                {titleLines[0]}
              </Text>
              {titleLines[1] && (
                <Text fontSize='hs' fontWeight='bold' color={textColor} lineHeight='1.2'>
                  {titleLines[1]}
                </Text>
              )}
            </Flex>
          </Flex>

          {/* Description */}
          <Text fontSize='sm' color={textColor} lineHeight='1.4' mt={2}>
            {description}
          </Text>

          {/* CTA label */}
          <Text
            as='span'
            fontSize='s'
            fontWeight='bold'
            color={ctaColor}
            display='inline-flex'
            alignItems='center'
            gap='4px'
            mt={2}
          >
            {ctaLabel}
            {' ↗'}
          </Text>
        </Flex>
      </Box>

      {badge && (
        <Box
          position='absolute'
          top='-5px'
          right='-9px'
          bg='#29dbec'
          color='#1a25cb'
          fontSize='sm'
          fontWeight='bold'
          borderRadius='full'
          px={2}
          py='2px'
          lineHeight='1.4'
          zIndex={1}
        >
          {badge}
        </Box>
      )}
    </Link>
  );
};
